import { SiteUrls } from '@tableau/shared-urls';
import { BodyCell, DataGrid } from '@tableau/widgets-datagrid';
import * as React from 'react';

import { CommonDataGridColumns } from '../../common/react/CommonDataGridColumns';
import { IActionTarget } from '../../common/react/components/ActionsMenu';
import { DataGridCells } from '../../common/react/components/dataGrid/DataGridCells';
import { SortableHeader } from '../../common/react/components/dataGrid/SortableHeader';
import { RightAlignedCell, RightAlignedHeader } from '../../common/react/components/dataGrid/StyledCellWrapper';
import { IReactTranslateService } from '../../common/react/IReactTranslateService';
import { L10n } from '../../ts/L10n/L10n';
import { ISiteUserInfo } from '../../ts/models/ISiteUserInfo';
import { SiteRoleOptionsResource } from '../../ts/resources/SiteRoleOptions';
import { SiteRoles } from '../../ts/ServerApi/types/ISiteRole';
import { IActionButton } from '../../ts/services/ActionPanelService';

// delete with Featureflags.stylishSiteUsers

export namespace SiteUsersGrid {

  export function columns({
    actions,
    getActionTarget,
    getUserAuthenticationInfo,
    scope,
    showAuthenticationColumn,
    showDomainNameColumn,
    siteUrls,
    translate,
    siteRoleOptionsResource
  }: {
    actions: IActionButton<ISiteUserInfo, {}>[];
    getActionTarget: () => IActionTarget<ISiteUserInfo, {}>;
    getUserAuthenticationInfo: (user: ISiteUserInfo) => {name: string, isEnabled: boolean};
    scope: DataGridCells.ISelectedItemsScope<ISiteUserInfo>;
    showAuthenticationColumn: boolean;
    showDomainNameColumn: boolean;
    siteUrls: SiteUrls;
    translate: IReactTranslateService;
    siteRoleOptionsResource: SiteRoleOptionsResource;
  }): DataGrid.Column[] {

    const columns: DataGrid.Column[] = [
      CommonDataGridColumns.selected(scope),
      CommonDataGridColumns.userDisplayNameColumn({
        actions: actions,
        getActionTarget: getActionTarget,
        linkDisabled: (user: ISiteUserInfo) => user.siteRole === SiteRoles.Guest,
        scope: scope,
        translate: translate,
        url: (user: ISiteUserInfo) => siteUrls.userContentUrl(user.username, user.domainName)
      }),
      {
        name: 'username-col',
        dataKey: 'username',
        headerText: translate('column_username'),
        headerFormatter: SortableHeader.Formatter(),
        direction: 'asc'
      },
      {
        name: 'site-role-col',
        dataKey: 'siteRole',
        headerText: translate('column_site_role'),
        direction: 'asc',
        headerFormatter: SortableHeader.Formatter(),
        cellFormatter: DataGridCells.SiteRoleCell('siteRole', translate, siteRoleOptionsResource)
      },
      {
        name: 'group-count-col',
        dataKey: 'groupCount',
        headerText: translate('column_groupCount'),
        headerFormatter: RightAlignedHeader(SortableHeader.Formatter()),
        cellFormatter: RightAlignedCell(DataGridCells.LocalizedCount('groupCount')),
        direction: 'asc',
      }
    ];

    if (showDomainNameColumn) {
      columns.push({
        name: 'domain-name-col',
        dataKey: 'domainName',
        direction: 'asc',
        headerFormatter: SortableHeader.Formatter(),
        headerText: translate('column_domainName')
      });
    }

    if (showAuthenticationColumn) {
      columns.push({
        name: 'authentication-col',
        dataKey: 'authentication',
        direction: 'asc',
        headerText: translate('column_authentication'),
        headerFormatter: SortableHeader.Formatter(),
        sortable: false,
        cellFormatter: DataGridCells.PlainTextCellWithIcon((row: any) => {
          const metadata = getUserAuthenticationInfo(<ISiteUserInfo>row);
          return {
            text: metadata.name,
            iconClass: metadata.isEnabled ? null : 'tb-icon-alert-list-header tb-react-dg-hcol',
            iconTitle: translate('disabled')
          };
        })
      });
    }

    columns.push({
      name: 'last-sign-in-col',
      dataKey: 'lastSignIn',
      headerText: translate('column_last_sign_in'),
      direction: 'desc',
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: LastSignInCell()
    });

    return columns;
  }

  function LastSignInCell(): React.StatelessComponent<BodyCell.Props> {
    return function LastSignInCell(props: BodyCell.Props) {
      return React.createElement('span', {}, L10n.localizedDateTime(props.row.data.lastSignIn));
    };
  }
}
